import { observable, action, makeObservable } from 'mobx'
import RootStore from './Root'
import { getWhiteLabelSite, getWhiteLabelSites, removeWhiteLabelPersona, saveWhiteLabelPersona, saveWhiteLabelSite } from '../api/WhiteLabel'

interface WhiteLabelSiteConfig {
    site_id: string
    site_key: string
    site_name: string
    site_domain: string
    site_title: string
    status: string
    logo_url: string
    favicon_url: string
    hero_title: string
    hero_subtitle: string
    primary_color: string
    secondary_color: string
    accent_color: string
    background_color: string
    text_color: string
}

interface WhiteLabelAvailablePersona {
    agent_key: string
    name: string
    status: string
    summary: string
    thumbnail_url: string
    tags: Array<string>
    is_assigned: boolean
}

interface WhiteLabelSiteOption {
    site_id: string
    site_name: string
    site_domain: string
    status: string
}

interface WhiteLabelAssignedPersona {
    site_id?: string
    agent_key: string
    display_name_override: string
    headline_override: string
    sort_order: number
    is_featured: boolean
    is_active: boolean
    qai_model?: {
        agent_key?: string
        name?: string
        status?: string
        summary?: string
    }
}

interface WhiteLabelRuntimePersona {
    agent_key: string
    name: string
    headline: string
    sort_order: number
    is_featured: boolean
    status: string
    summary: string
    thumbnail_url: string
    tags: Array<string>
}

interface WhiteLabelRuntimePreview {
    site: {
        site_key: string
        site_name: string
        site_domain: string
        site_title: string
        status: string
        logo_url: string
        favicon_url: string
        hero_title: string
        hero_subtitle: string
        theme: {
            primary_color: string
            secondary_color: string
            accent_color: string
            background_color: string
            text_color: string
        }
    }
    personas: Array<WhiteLabelRuntimePersona>
}

const DEFAULT_SITE_ID = 'phase1primary01'

const getEmptySiteConfig = (siteId = ''): WhiteLabelSiteConfig => ({
    site_id: siteId,
    site_key: siteId,
    site_name: '',
    site_domain: '',
    site_title: '',
    status: 'draft',
    logo_url: '',
    favicon_url: '',
    hero_title: '',
    hero_subtitle: '',
    primary_color: '#1f6fff',
    secondary_color: '#111827',
    accent_color: '#f97316',
    background_color: '#ffffff',
    text_color: '#111827'
})

class WhiteLabelStore {
    public rootStore: RootStore
    @observable public apiErrorMessage: string
    @observable public isApiError: boolean
    @observable public isLoading: boolean
    @observable public isSavingSite: boolean
    @observable public isSavingPersona: boolean
    @observable public successMessage: string
    @observable public selectedSiteId: string
    @observable public siteOptions: Array<WhiteLabelSiteOption>
    @observable public siteConfig: WhiteLabelSiteConfig
    @observable public availablePersonas: Array<WhiteLabelAvailablePersona>
    @observable public assignedPersonas: Array<WhiteLabelAssignedPersona>
    @observable public runtimePreview: WhiteLabelRuntimePreview | null

    constructor(rootStore: RootStore) {
        makeObservable(this)
        this.rootStore = rootStore
        this.apiErrorMessage = ''
        this.isApiError = false
        this.isLoading = false
        this.isSavingSite = false
        this.isSavingPersona = false
        this.successMessage = ''
        this.selectedSiteId = ''
        this.siteOptions = []
        this.siteConfig = getEmptySiteConfig()
        this.availablePersonas = []
        this.assignedPersonas = []
        this.runtimePreview = null
    }

    @action.bound
    clearMessages() {
        this.isApiError = false
        this.apiErrorMessage = ''
        this.successMessage = ''
    }

    @action.bound
    applyResponseData(data: any) {
        this.selectedSiteId = data.selected_site_id || this.selectedSiteId
        this.siteOptions = data.sites || []
        this.siteConfig = data.site || getEmptySiteConfig(this.selectedSiteId)
        this.availablePersonas = data.available_personas || []
        this.assignedPersonas = data.assigned_personas || []
        this.runtimePreview = data.runtime_preview || null
    }

    @action.bound
    setSelectedSite(siteId: string) {
        this.selectedSiteId = siteId
        this.getWhiteLabelSiteData(siteId)
    }

    @action.bound
    initializeNewSite() {
        this.clearMessages()
        this.selectedSiteId = ''
        this.siteConfig = getEmptySiteConfig()
        this.availablePersonas = this.availablePersonas.map((item) => ({
            ...item,
            is_assigned: false
        }))
        this.assignedPersonas = []
        this.runtimePreview = null
    }

    @action.bound
    getWhiteLabelSiteData(siteId?: string) {
        this.clearMessages()
        this.isLoading = true
        const requestedSiteId = siteId || this.selectedSiteId || DEFAULT_SITE_ID
        getWhiteLabelSite(requestedSiteId).then((response) => {
            this.isLoading = false
            if (response.data.success === 0) {
                this.isApiError = true
                this.apiErrorMessage = response.data.message
                return
            }

            this.applyResponseData(response.data.data)
        }).catch((error) => {
            this.isLoading = false
            this.isApiError = true
            this.apiErrorMessage = error.message || 'There was a problem fetching white-label site data.'
        })
    }

    @action.bound
    getWhiteLabelSiteOptions() {
        getWhiteLabelSites().then((response) => {
            if (response.data.success === 1) {
                const rows = response.data.data.rows || []
                this.siteOptions = rows

                if (rows.length === 0) {
                    this.selectedSiteId = ''
                    this.siteConfig = getEmptySiteConfig()
                    return
                }

                const currentSiteStillExists = rows.some((row: WhiteLabelSiteOption) => row.site_id === this.selectedSiteId)
                const nextSelectedSiteId = currentSiteStillExists ? this.selectedSiteId : rows[0].site_id

                if (nextSelectedSiteId !== this.selectedSiteId) {
                    this.selectedSiteId = nextSelectedSiteId
                    this.getWhiteLabelSiteData(nextSelectedSiteId)
                } else if (this.siteConfig.site_id !== nextSelectedSiteId) {
                    this.getWhiteLabelSiteData(nextSelectedSiteId)
                }
            }
        }).catch(() => null)
    }

    @action.bound
    saveSiteConfig(cb?: (success: boolean) => void) {
        this.clearMessages()
        this.isSavingSite = true
        saveWhiteLabelSite(this.siteConfig).then((response) => {
            this.isSavingSite = false
            if (response.data.success === 0) {
                this.isApiError = true
                this.apiErrorMessage = response.data.message
                cb?.(false)
                return
            }

            this.applyResponseData(response.data.data)
            this.getWhiteLabelSiteOptions()
            this.successMessage = response.data.message
            cb?.(true)
        }).catch((error) => {
            this.isSavingSite = false
            this.isApiError = true
            this.apiErrorMessage = error.message || 'There was a problem saving white-label site data.'
            cb?.(false)
        })
    }

    @action.bound
    saveAssignedPersona(persona: WhiteLabelAssignedPersona | WhiteLabelAvailablePersona, cb?: (success: boolean) => void) {
        this.clearMessages()
        this.isSavingPersona = true
        const payload = {
            site_id: (persona as WhiteLabelAssignedPersona).site_id || this.siteConfig.site_id || this.selectedSiteId,
            agent_key: persona.agent_key,
            display_name_override: (persona as WhiteLabelAssignedPersona).display_name_override || '',
            headline_override: (persona as WhiteLabelAssignedPersona).headline_override || '',
            sort_order: (persona as WhiteLabelAssignedPersona).sort_order || 0,
            is_featured: (persona as WhiteLabelAssignedPersona).is_featured || false,
            is_active: typeof (persona as WhiteLabelAssignedPersona).is_active === 'boolean' ? (persona as WhiteLabelAssignedPersona).is_active : true
        }

        saveWhiteLabelPersona(payload).then((response) => {
            this.isSavingPersona = false
            if (response.data.success === 0) {
                this.isApiError = true
                this.apiErrorMessage = response.data.message
                cb?.(false)
                return
            }

            this.applyResponseData(response.data.data)
            this.successMessage = response.data.message
            cb?.(true)
        }).catch((error) => {
            this.isSavingPersona = false
            this.isApiError = true
            this.apiErrorMessage = error.message || 'There was a problem saving the white-label persona assignment.'
            cb?.(false)
        })
    }

    @action.bound
    removeAssignedPersona(agentKey: string, cb?: (success: boolean) => void) {
        this.clearMessages()
        this.isSavingPersona = true

        removeWhiteLabelPersona(this.siteConfig.site_id || this.selectedSiteId, agentKey).then((response) => {
            this.isSavingPersona = false
            if (response.data.success === 0) {
                this.isApiError = true
                this.apiErrorMessage = response.data.message
                cb?.(false)
                return
            }

            this.applyResponseData(response.data.data)
            this.successMessage = response.data.message
            cb?.(true)
        }).catch((error) => {
            this.isSavingPersona = false
            this.isApiError = true
            this.apiErrorMessage = error.message || 'There was a problem removing the white-label persona assignment.'
            cb?.(false)
        })
    }
}

export default WhiteLabelStore
